// 파티 판 연결(docs/PARTY-TRADE-PRD.md 3장, 레인 D): PartyClient(네트워크) ↔ GameSim(고정 60Hz) 연결점. game.party가 이것이다.
// - 'go' 사건 → 로컬 판 「파티 토벌전」을 연다: 지역1 지도(MAPS.r1) 보스방 아레나, 장면 Dungeon, 로컬 웨이브·방 진행 끔, 문 모두 닫힘,
//   영웅은 PARTY_ARENA.spawns[내 칸].
// - 판 동안 로컬 몹은 서버 몹의 꼭두각시다: 서버 id ↔ 로컬 슬롯 표(고정 배열), 위치·방향·체력·공격 준비는 서버 값(MobController.puppetStep),
//   몹 AI 이동·로컬 공격 결정은 돌지 않는다. atk → 지면 경고, hurt(내 칸) → 기존 영웅 피격 경로(회피 무적이면 무시 — 클라 권위).
// - 내 타격은 combat.damageMob이 hit(서버 id, 피해)로 보내고, 로컬 체력은 서버 die 전까지 1 아래로 내려가지 않는다. die → killMob 처치 연출.
//   로컬 드롭·EXP·처치 보상은 끈다(보상은 서버 우편).
// - 내 영웅이 쓰러지면 down() → PARTY_REVIVE_MS 뒤 그 자리에서 일어나고 up()(패배 화면으로 가지 않는다).
// - end → 결과(view.result, UI 레인이 결과 창을 띄운다) → 4초 뒤 마을. 연결이 끝내 끊기면 마을.
// 고정 스텝·프레임 경로에서 새 객체를 만들지 않는다(사건 콜백·순회 콜백은 생성자에서 한 번 만든다).
import { MOB_CAPACITY } from '../shared/config';
import { Ev, TeleSource } from '../shared/events';
import { makePeers, type FieldPeer } from '../shared/fieldNet';
import { MAPS } from '../shared/levels';
import { freePlan, type StagePlan } from '../shared/content/stages';
import { REVIVE } from '../shared/content/consumables';
import { PARTY_ARENA, PARTY_REVIVE_MS, PMobSt } from '../shared/partyNet';
import type { PartyResultView, PartyView } from '../shared/partyView';
import { HeroAct, MobState, Scene, TeleShape } from '../shared/types';
import { HitSource } from './combat';
import type { GameSim } from './game';
import type { PartyClient, PartyEvent, PartyTrack } from './partyNet';

const TICK_HZ = 60;
/** 서버 몹 id 공간(판마다 1부터 — 한 판 몹 ≤ 20). 하위 8비트로 표를 찾고 idOf로 확인한다 */
const ID_SPACE = 256;
/** 내 타격 직후 서버 스냅이 오기 전까지 로컬 체력(예측)을 보이는 틱(서버가 버린 타격이면 이 뒤 서버 값으로 돌아간다) */
const PRED_TICKS = 30;
/** 결과 뒤 마을로 */
export const PARTY_END_LEAVE_TICKS = 4 * TICK_HZ;
/** 다시 붙기를 이만큼 이어도 안 되면 판을 버리고 마을로(서버 자리 유지 60초 + 여유) */
const RETRY_GIVE_UP_TICKS = 65 * TICK_HZ;
/** 로비 복귀 뒤 결과(end)를 기다리는 틱(장부 쓰기 재시도 1초 간격 × 몇 번) */
export const LOBBY_WAIT_TICKS = 8 * TICK_HZ;
const REVIVE_TICKS = Math.round((PARTY_REVIVE_MS / 1000) * TICK_HZ);
export const PARTY_LABEL = '파티 토벌전';
export const PARTY_OBJECTIVE = '동료와 함께 몰려오는 몬스터를 막아내세요.';
const PEER_SLOTS = 4;

class PartyViewState implements PartyView {
  active = false;
  attached = false;
  wave = 0;
  waves = 0;
  reviveTicks = 0;
  spawned = 0;
  killed = 0;
  hitsSent = 0;
  hurtTaken = 0;
  readonly peers: FieldPeer[] = makePeers(PEER_SLOTS);
  result: PartyResultView | null = null;
  readonly resultBox: PartyResultView = { seq: 0, ok: false, reason: '', kills: 0, ms: 0, eligible: false, gold: 0, items: 0, leaveTicks: 0 };
}

export class PartyLink {
  client: PartyClient | null = null;
  readonly view = new PartyViewState();
  /** 로컬 몹 슬롯 → 서버 id(-1 = 꼭두각시 아님) */
  readonly idOf = new Int32Array(MOB_CAPACITY).fill(-1);
  /** 서버 보간 위치·방향·상태(PMobSt) — MobController.puppetStep이 읽는다 */
  readonly tx = new Float32Array(MOB_CAPACITY);
  readonly tz = new Float32Array(MOB_CAPACITY);
  readonly tf = new Float32Array(MOB_CAPACITY);
  readonly sst = new Uint8Array(MOB_CAPACITY);
  private readonly slotById = new Int16Array(ID_SPACE).fill(-1);
  private readonly predHp = new Float64Array(MOB_CAPACITY);
  private readonly predTicks = new Uint16Array(MOB_CAPACITY);
  /** 몹별 지면 경고 슬롯과 남은 틱 */
  private readonly tele = new Int16Array(MOB_CAPACITY).fill(-1);
  private readonly teleLeft = new Uint16Array(MOB_CAPACITY);
  private readonly plan: StagePlan;
  private retryTicks = 0;
  private profileTicks = 0;
  private ended = false;
  private lobbyTicks = 0;
  private readonly onEvent = (e: PartyEvent): void => this.handle(e);
  private readonly eachMob = (k: PartyTrack): void => this.syncMob(k);
  private readonly eachPeer = (k: PartyTrack): void => this.syncPeer(k);

  constructor(private readonly g: GameSim) {
    // 판 계획: 지역1 자유 입장 계획에서 방 진행만 뺀다(웨이브·보스·방 잠금 없음). 몹 체력·피해는 서버 값이 이긴다
    this.plan = { ...freePlan('r1'), rooms: [] };
  }

  /** 파티 판 진행 중 */
  get active(): boolean { return this.view.active; }

  /** UI 레인이 만든 연결층을 붙인다(하나만). null이면 뗀다(진행 중 판은 마을로) */
  attach(client: PartyClient | null): void {
    if (this.client === client) return;
    if (this.view.active) this.leave();
    this.client = client;
    this.view.attached = client !== null;
  }

  /** 로컬 슬롯 i가 서버 몹 꼭두각시인가 */
  owns(i: number): boolean { return this.view.active && this.idOf[i] >= 0; }

  /**
   * 내 타격(combat.damageMob·hazardMob): 서버로 보내고 체력 예측을 잠깐 유지한다. 꼭두각시가 아니면 false(호출자가 원래 규칙대로)
   */
  hit(i: number, damage: number): boolean {
    const id = this.idOf[i];
    if (!this.view.active || id < 0 || !this.client) return false;
    if (damage > 0) { this.client.hit(id, damage); this.view.hitsSent++; }
    this.predHp[i] = this.g.mobs.hp[i]; this.predTicks[i] = PRED_TICKS;
    return true;
  }

  /** 내 영웅이 쓰러졌다(flow.defeat 대신): 패배 화면 없이 PARTY_REVIVE_MS 뒤 그 자리에서 일어난다 */
  heroDown(): void {
    const g = this.g, h = g.hero;
    h.hp = 0; h.act = HeroAct.Dead; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.awakening.cancel();
    g.skillFx.clear();
    g.arrows.clear();
    this.view.reviveTicks = REVIVE_TICKS;
    this.client?.down();
    g.flow.objectiveLabel = '쓰러졌다! 잠시 뒤 그 자리에서 일어난다.';
  }

  /** 고정 스텝마다(GameSim.step): 사건 처리 → 서버 몹 적용 → 부활·결과 타이머 → 내 위치 송신 */
  step(): void {
    const c = this.client;
    if (!c) return;
    c.drain(this.onEvent);
    const v = this.view;
    // 로비 대기 중 영웅·레벨 변경 동기화(1초마다 비교만 — 바뀌었을 때만 보낸다)
    if (++this.profileTicks >= 60) { this.profileTicks = 0; c.syncProfile(); }
    if (!v.active) return;
    const g = this.g, h = g.hero;
    // 판이 사라졌다: 끊김(다시 붙기 포기)·연결 종료·서버 방이 로비로 돌아감(끝 사건 없이)
    if (c.status === 'closed') { this.leave(); return; }
    if (c.status === 'retrying') { if (++this.retryTicks > RETRY_GIVE_UP_TICKS) { c.close(); this.leave(); return; } }
    else this.retryTicks = 0;
    // 서버 방이 로비로 돌아갔는데 결과(end)가 아직 없다: 정상 종료는 장부를 쓴 뒤 end가 오므로 잠시 기다린다(PR #243 Codex P2).
    // LOBBY_WAIT_TICKS가 지나도 end가 없으면(서버 재시작 등) 마을로
    if (!this.ended && c.status === 'online' && c.room && c.room.phase === 'lobby') { if (++this.lobbyTicks > LOBBY_WAIT_TICKS) { this.leave(); return; } }
    else this.lobbyTicks = 0;
    if (this.ended) {
      const r = v.resultBox;
      if (r.leaveTicks > 0 && --r.leaveTicks === 0) { this.leave(); return; }
    }
    c.sample();
    c.mobs.forEach(this.eachMob);
    // 지면 경고 진행(판정 사건 hurt가 오면 터진다. 오지 않으면 길이 + 0.5초 뒤 지운다)
    for (let i = 0; i < MOB_CAPACITY; i++) {
      const t = this.tele[i];
      if (t < 0) continue;
      const tg = g.telegraphs[t];
      if (tg.tick < tg.total) tg.tick++;
      if (this.teleLeft[i] > 0 && --this.teleLeft[i] === 0) { g.releaseTelegraph(t); this.tele[i] = -1; }
    }
    // 부활(파티 판 전용 규칙)
    if (v.reviveTicks > 0 && --v.reviveTicks === 0 && h.act === HeroAct.Dead) {
      h.hp = Math.max(1, Math.round(h.maxHp * REVIVE.hp));
      g.heroController.revive(REVIVE.invulnTicks);
      const e = g.emit(Ev.HeroHeal); e.x = h.x; e.z = h.z; e.a = h.hp; e.b = 0;
      c.up();
      g.flow.objectiveLabel = PARTY_OBJECTIVE;
    }
    c.update(h.x, h.z, h.facing, h.act);
  }

  /** 렌더 프레임마다(main.ts): 동료 영웅 보간 구간을 view.peers에 옮긴다(PeersVisual이 performance.now로 샘플) */
  frame(): void {
    const peers = this.view.peers;
    for (let i = 0; i < peers.length; i++) { peers[i].active = false; peers[i].visible = false; }
    const c = this.client;
    if (!c || !this.view.active) return;
    c.peers.forEach(this.eachPeer);
  }

  // ── 사건 ──────────────────────────────────────────
  private handle(e: PartyEvent): void {
    if (e.t === 'go') { this.start(); return; }
    if (e.t === 'end') { this.finish(e); return; }
    if (!this.view.active) return;
    const g = this.g;
    switch (e.t) {
      case 'wave':
        this.view.wave = e.n; this.view.waves = e.of;
        g.flow.wave = e.n - 1; g.flow.waveCount = e.of;
        g.flow.stageLabel = `${PARTY_LABEL} · 웨이브 ${e.n}/${e.of}`;
        g.emit(Ev.WaveStart).a = e.n - 1;
        break;
      case 'atk': {
        const i = this.slotOf(e.id);
        if (i < 0) break;
        if (this.tele[i] >= 0) g.releaseTelegraph(this.tele[i]);
        const t = g.allocateTelegraph(TeleSource.Mob, i);
        this.tele[i] = t;
        if (t < 0) break;
        const tg = g.telegraphs[t];
        tg.shape = TeleShape.Circle; tg.x = e.x; tg.z = e.z; tg.radius = e.r; tg.angle = 0; tg.width = 0; tg.tick = 0;
        tg.total = Math.max(1, Math.round((e.ms / 1000) * TICK_HZ));
        this.teleLeft[i] = tg.total + 30;
        break;
      }
      case 'hurt': {
        const i = this.slotOf(e.id);
        const m = g.mobs;
        let fx = g.hero.x, fz = g.hero.z - 1;
        if (i >= 0) {
          fx = m.x[i]; fz = m.z[i];
          if (this.tele[i] >= 0) { g.fireTelegraph(this.tele[i]); this.tele[i] = -1; this.teleLeft[i] = 0; }
        }
        const you = this.client ? this.client.you : -1;
        let me = false;
        for (let k = 0; k < e.slots.length; k++) if (e.slots[k] === you) { me = true; break; }
        // 내 칸이 판정 원 안이었다: 기존 영웅 피격 경로(회피 무적·방어·보호막·세트 버팀은 클라 규칙)
        if (me && g.hero.hp > 0 && g.hero.act !== HeroAct.Dead) {
          if (g.combat.hurtHero(e.d, fx, fz, i >= 0 && m.elite[i] !== 0, 2)) this.view.hurtTaken++;
        }
        break;
      }
      case 'die': {
        const i = this.slotOf(e.id);
        if (i < 0) break;
        this.unmap(i);
        const st = g.mobs.state[i];
        if (st !== MobState.Inactive && st !== MobState.Dying) g.combat.killMob(i, HitSource.Basic, true);
        this.view.killed++;
        break;
      }
      case 'correct':
        g.snapHero(e.x, e.z);
        break;
      default: break;
    }
  }

  /** 'go': 로컬 판을 연다 */
  private start(): void {
    const g = this.g, c = this.client;
    if (!c) return;
    if (this.view.active) this.clearMobs();
    g.flowController.loadMap('r1', null, { scene: Scene.Dungeon, plan: this.plan, map: MAPS.r1, grid: g.grids.r1, label: PARTY_LABEL, objective: PARTY_OBJECTIVE, kind: 'party' });
    const f = g.flow;
    if (f.scene !== Scene.Dungeon) return; // 보상 카드를 고르는 중 등 — 장면이 거절했다(서버는 내 자리를 비워 둔다)
    const v = this.view;
    v.active = true; v.wave = 0; v.waves = 0; v.reviveTicks = 0; v.spawned = 0; v.killed = 0; v.hitsSent = 0; v.hurtTaken = 0;
    v.result = null;
    this.ended = false; this.retryTicks = 0; this.lobbyTicks = 0;
    this.clearMobs();
    // 로컬 방 진행 끔: 모든 방을 정리됨으로(방 진입·웨이브 스폰이 일어나지 않는다), 문은 모두 닫는다(보스방 입구 포함)
    f.roomState.fill(2);
    f.gateOpen.fill(0); g.flowController.gateTarget.fill(0);
    f.stageKind = 'party'; f.stageId = '';
    const sp = PARTY_ARENA.spawns[Math.max(0, Math.min(PARTY_ARENA.spawns.length - 1, c.you))];
    g.snapHero(sp.x, sp.z);
    g.hero.facing = Math.PI; // 몹은 아레나 남쪽(z 작은 쪽) 입구에서 온다
  }

  private finish(e: Extract<PartyEvent, { t: 'end' }>): void {
    const v = this.view, r = v.resultBox;
    r.seq++; r.ok = e.ok; r.reason = e.reason; r.kills = e.kills; r.ms = e.ms; r.eligible = e.eligible;
    r.gold = e.reward ? e.reward.gold : 0; r.items = e.reward ? e.reward.items : 0;
    r.leaveTicks = v.active ? PARTY_END_LEAVE_TICKS : 0;
    v.result = r;
    if (!v.active) return;
    this.ended = true;
    const g = this.g;
    g.flow.objectiveLabel = e.ok ? (r.gold > 0 || r.items > 0 ? '토벌 성공! 보상은 우편함으로 보냈다.' : '토벌 성공!') : '토벌 실패. 마을로 돌아간다.';
    g.result.cleared = e.ok; g.result.clearTicks = Math.round((e.ms / 1000) * TICK_HZ);
    if (e.ok && g.hero.act !== HeroAct.Dead) { g.hero.act = HeroAct.Victory; g.hero.actTick = 0; }
    this.clearTelegraphs();
  }

  /** 판을 접고 마을로 */
  private leave(): void {
    const g = this.g, v = this.view;
    const was = v.active;
    v.active = false; v.reviveTicks = 0;
    this.ended = false;
    this.clearMobs();
    if (v.result) v.result.leaveTicks = 0;
    if (was && g.flow.scene === Scene.Dungeon && g.flow.stageKind === 'party') g.flowController.loadMap('village');
  }

  // ── 서버 몹 ↔ 로컬 슬롯 ─────────────────────────────
  private slotOf(id: number): number {
    const i = this.slotById[id & (ID_SPACE - 1)];
    return i >= 0 && this.idOf[i] === id ? i : -1;
  }

  private unmap(i: number): void {
    const id = this.idOf[i];
    if (id >= 0 && this.slotById[id & (ID_SPACE - 1)] === i) this.slotById[id & (ID_SPACE - 1)] = -1;
    this.idOf[i] = -1;
    this.g.mobController.puppet[i] = 0;
    if (this.tele[i] >= 0) { this.g.releaseTelegraph(this.tele[i]); this.tele[i] = -1; }
    this.teleLeft[i] = 0; this.predTicks[i] = 0;
  }

  private clearTelegraphs(): void {
    for (let i = 0; i < MOB_CAPACITY; i++) if (this.tele[i] >= 0) { this.g.releaseTelegraph(this.tele[i]); this.tele[i] = -1; this.teleLeft[i] = 0; }
  }

  private clearMobs(): void {
    for (let i = 0; i < MOB_CAPACITY; i++) if (this.idOf[i] >= 0) this.unmap(i);
    this.slotById.fill(-1);
    this.g.mobController.puppet.fill(0);
  }

  /** 서버 몹 하나(보간 뒤): 처음 보면 로컬 슬롯을 얻고, 위치·상태·체력을 옮긴다 */
  private syncMob(k: PartyTrack): void {
    if (k.st === PMobSt.Dead || k.hp <= 0) return;
    const g = this.g, m = g.mobs;
    let i = this.slotOf(k.id);
    if (i < 0) {
      if (this.ended) return;
      i = g.mobController.spawn(k.kind, k.elite, k.x, k.z);
      if (i < 0) return; // 자리·용량이 없다: 다음 틱에 다시
      this.idOf[i] = k.id; this.slotById[k.id & (ID_SPACE - 1)] = i;
      g.mobController.puppet[i] = 1;
      m.facing[i] = k.f;
      this.view.spawned++;
    }
    this.tx[i] = k.x; this.tz[i] = k.z; this.tf[i] = k.f; this.sst[i] = k.st;
    m.maxHp[i] = Math.max(1, k.max);
    const server = Math.max(1, k.hp);
    if (this.predTicks[i] > 0) { this.predTicks[i]--; m.hp[i] = Math.max(1, Math.min(server, this.predHp[i])); }
    else m.hp[i] = server;
  }

  /** 동료 하나(칸 = k.id): PartyTrack 보간 구간 → FieldPeer(PeersVisual 입력) */
  private syncPeer(k: PartyTrack): void {
    const slot = k.id;
    if (slot < 0 || slot >= PEER_SLOTS || !k.seen) return;
    const p = this.view.peers[slot];
    p.active = true; p.visible = true; p.id = slot;
    p.t0 = k.at; p.x0 = k.ax; p.z0 = k.az; p.f0 = k.af;
    p.t1 = k.bt; p.x1 = k.bx; p.z1 = k.bz; p.f1 = k.bf;
    p.act = k.st;
    const room = this.client?.room;
    if (!room) return;
    for (let j = 0; j < room.members.length; j++) {
      const mem = room.members[j];
      if (mem.slot !== slot) continue;
      if (p.name !== mem.name) { p.name = mem.name; p.serial++; }
      p.hero = mem.hero === 'lyra' ? 1 : 0;
      break;
    }
  }
}

